/**
 * The patient app's icons, drawn to the 23 September mockups.
 *
 * The shared set in `components/icons.tsx` is the clinic's thin line. The
 * phone screens need a few shapes it does not have (the filled tab icons, the
 * red bullseye, the open book, the speech bubble, the bars drawn as boxes)
 * and a slightly heavier stroke, because a 20px glyph on a phone reads thinner
 * than the same glyph in a desktop sidebar. Same 24 unit grid, same
 * `currentColor`, so a colour class is all a caller needs.
 */

import type { ReactNode } from 'react';

type IconProps = { size?: number; className?: string; strokeWidth?: number };

function line(children: ReactNode, { size = 22, className = '', strokeWidth = 1.8 }: IconProps) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      className={className}
      aria-hidden="true"
    >
      {children}
    </svg>
  );
}

function solid(children: ReactNode, { size = 22, className = '' }: IconProps) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="currentColor" className={className} aria-hidden="true">
      {children}
    </svg>
  );
}

/* ------------------------------------------------------------------ *
 * Tab bar, outline and filled (the active tab is filled, as drawn)
 * ------------------------------------------------------------------ */

export const HomeLine = (p: IconProps) =>
  line(<path d="M3.6 10.4 12 3.8l8.4 6.6v9.4a1 1 0 0 1-1 1h-4.6v-6.2H9.2v6.2H4.6a1 1 0 0 1-1-1z" />, p);

export const HomeSolid = (p: IconProps) =>
  solid(
    <path d="M12.62 3.02a1 1 0 0 0-1.24 0L3.2 9.45A1.6 1.6 0 0 0 2.6 10.7v8.9a1.8 1.8 0 0 0 1.8 1.8h4.5a.6.6 0 0 0 .6-.6v-4.9a2.5 2.5 0 0 1 5 0v4.9a.6.6 0 0 0 .6.6h4.5a1.8 1.8 0 0 0 1.8-1.8v-8.9a1.6 1.6 0 0 0-.6-1.25z" />,
    p,
  );

export const HeartLine = (p: IconProps) =>
  line(<path d="M12 20.2S3.4 15 3.4 9.1A4.7 4.7 0 0 1 12 6.4a4.7 4.7 0 0 1 8.6 2.7c0 5.9-8.6 11.1-8.6 11.1z" />, p);

export const HeartSolid = (p: IconProps) =>
  solid(<path d="M12 21.2c-.2 0-.4-.05-.55-.15C10.9 20.7 2.4 15.4 2.4 9.1a5.7 5.7 0 0 1 9.6-4.2 5.7 5.7 0 0 1 9.6 4.2c0 6.3-8.5 11.6-9.05 11.95-.15.1-.35.15-.55.15z" />, p);

/** Three bars drawn as boxes, the mockup's "Progress" glyph. */
export const BarsLine = (p: IconProps) =>
  line(
    <>
      <rect x="4" y="13.2" width="3.8" height="7" rx="0.9" />
      <rect x="10.1" y="9" width="3.8" height="11.2" rx="0.9" />
      <rect x="16.2" y="3.8" width="3.8" height="16.4" rx="0.9" />
    </>,
    p,
  );

export const BarsSolid = (p: IconProps) =>
  solid(
    <>
      <rect x="3.6" y="12.6" width="4.4" height="7.6" rx="0.8" />
      <rect x="9.8" y="8.4" width="4.4" height="11.8" rx="0.8" />
      <rect x="16" y="3.4" width="4.4" height="16.8" rx="0.8" />
      <rect x="2.8" y="20" width="18.4" height="1.6" rx="0.8" />
    </>,
    p,
  );

export const DocLine = (p: IconProps) =>
  line(
    <>
      <path d="M14 3H7a1.8 1.8 0 0 0-1.8 1.8v14.4A1.8 1.8 0 0 0 7 21h10a1.8 1.8 0 0 0 1.8-1.8V7.8z" />
      <path d="M14 3v4.8h4.8M9 12.4h6M9 16h6" />
    </>,
    p,
  );

export const DocSolid = (p: IconProps) =>
  solid(
    <path
      fillRule="evenodd"
      d="M7 2.2A2.6 2.6 0 0 0 4.4 4.8v14.4A2.6 2.6 0 0 0 7 21.8h10a2.6 2.6 0 0 0 2.6-2.6V8.3a1 1 0 0 0-.3-.7l-5-5a1 1 0 0 0-.7-.4zm2 9.4a.8.8 0 0 0 0 1.6h6a.8.8 0 0 0 0-1.6zm0 3.6a.8.8 0 0 0 0 1.6h6a.8.8 0 0 0 0-1.6z"
    />,
    p,
  );

export const UserLine = (p: IconProps) =>
  line(
    <>
      <circle cx="12" cy="7.8" r="3.9" />
      <path d="M4.2 20.2c.3-3.8 3.6-6.4 7.8-6.4s7.5 2.6 7.8 6.4a.6.6 0 0 1-.6.6H4.8a.6.6 0 0 1-.6-.6z" />
    </>,
    p,
  );

export const UserSolid = (p: IconProps) =>
  solid(
    <>
      <circle cx="12" cy="7.8" r="4.4" />
      <path d="M3.4 20.1c.35-4.3 4.1-7.2 8.6-7.2s8.25 2.9 8.6 7.2a1.4 1.4 0 0 1-1.4 1.5H4.8a1.4 1.4 0 0 1-1.4-1.5z" />
    </>,
    p,
  );

/* ------------------------------------------------------------------ *
 * Header
 * ------------------------------------------------------------------ */

export const Bell = (p: IconProps) =>
  line(
    <>
      <path d="M12 2.9v1.4" />
      <path d="M6.1 16.8v-5.6a5.9 5.9 0 0 1 11.8 0v5.6l1.7 1.9H4.4z" />
      <path d="M10 21a2.2 2.2 0 0 0 4 0" />
    </>,
    p,
  );

export const Menu = (p: IconProps) => line(<path d="M3.5 6.2h17M3.5 12h17M3.5 17.8h17" />, p);

export const Close = (p: IconProps) => line(<path d="M6 6l12 12M18 6 6 18" />, p);

export const ArrowLeft = (p: IconProps) => line(<path d="M20 12H4.6M10.4 5.8 4.2 12l6.2 6.2" />, p);

export const ArrowRight = (p: IconProps) => line(<path d="M4 12h15.4M13.6 5.8l6.2 6.2-6.2 6.2" />, p);

export const ArrowUp = (p: IconProps) => line(<path d="M12 20V4.6M5.8 10.4 12 4.2l6.2 6.2" />, p);

export const ArrowDown = (p: IconProps) => line(<path d="M12 4v15.4M5.8 13.6l6.2 6.2 6.2-6.2" />, p);

export const Chevron = (p: IconProps) => line(<path d="m9 5.4 6.6 6.6L9 18.6" />, p);

export const ChevronDown = (p: IconProps) => line(<path d="M5.4 9 12 15.6 18.6 9" />, p);

/** A small solid triangle, the "UP 18" marker in the score pills. */
export const TriangleUp = ({ size = 10, className = '' }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 10 10" fill="currentColor" className={className} aria-hidden="true">
    <path d="M5 1.2 9.4 8.8H.6z" />
  </svg>
);

export const TriangleDown = ({ size = 10, className = '' }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 10 10" fill="currentColor" className={className} aria-hidden="true">
    <path d="M5 8.8.6 1.2h8.8z" />
  </svg>
);

/* ------------------------------------------------------------------ *
 * Content
 * ------------------------------------------------------------------ */

/** The red bullseye on the goal card: two filled rings, a centre and a dart. */
export const Bullseye = ({ size = 30, className = '' }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 32 32" fill="none" className={className} aria-hidden="true">
    <circle cx="15" cy="17" r="12" stroke="currentColor" strokeWidth="3.4" />
    <circle cx="15" cy="17" r="6.4" stroke="currentColor" strokeWidth="3.2" />
    <circle cx="15" cy="17" r="2" fill="currentColor" />
    <path d="M15.6 16.4 26.4 5.6" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    <path d="M23.6 3.4v4.4h4.4" stroke="currentColor" strokeWidth="2.6" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

export const Flask = (p: IconProps) =>
  line(
    <>
      <path d="M9.2 3h5.6M10.2 3v6.1L5.1 17.7A2.2 2.2 0 0 0 7 21h10a2.2 2.2 0 0 0 1.9-3.3l-5.1-8.6V3" />
      <path d="M10.4 14.6a1.2 1.2 0 1 0 1.5 1.1" />
    </>,
    p,
  );

export const ChatBubble = (p: IconProps) =>
  line(<path d="M12 4c4.7 0 8.4 3 8.4 6.9s-3.7 6.9-8.4 6.9c-.9 0-1.8-.1-2.6-.3L5 20l.8-3.9c-1.4-1.3-2.2-3-2.2-5.2C3.6 7 7.3 4 12 4z" />, p);

export const Book = (p: IconProps) =>
  line(
    <>
      <path d="M12 6.4c-1.9-1.4-4.8-1.9-8.2-1.6v13.6c3.4-.3 6.3.2 8.2 1.6 1.9-1.4 4.8-1.9 8.2-1.6V4.8c-3.4-.3-6.3.2-8.2 1.6z" />
      <path d="M12 6.4V20" />
    </>,
    p,
  );

export const Calendar = (p: IconProps) =>
  line(
    <>
      <rect x="4" y="5.4" width="16" height="14.6" rx="1.8" />
      <path d="M4 9.8h16M8.4 3.6v3.4M15.6 3.6v3.4" />
      <path d="M8.2 13.2h.01M12 13.2h.01M15.8 13.2h.01M8.2 16.6h.01M12 16.6h.01" strokeWidth="2.2" />
    </>,
    p,
  );

export const Users = (p: IconProps) =>
  line(
    <>
      <circle cx="12" cy="8.2" r="3" />
      <path d="M6.6 19.2c0-3.1 2.4-5.4 5.4-5.4s5.4 2.3 5.4 5.4" />
      <circle cx="5.9" cy="9.6" r="2.3" />
      <path d="M2.4 18c0-2.3 1.5-4 3.6-4.3" />
      <circle cx="18.1" cy="9.6" r="2.3" />
      <path d="M21.6 18c0-2.3-1.5-4-3.6-4.3" />
    </>,
    p,
  );

export const LeafSolid = ({ size = 22, className = '' }: IconProps) => (
  <svg width={size} height={size} viewBox="0 0 24 24" className={className} aria-hidden="true">
    <path
      fill="currentColor"
      d="M20.6 3.2c-8.4.2-14.2 4.4-14.8 11.2-.1 1.5.1 2.8.6 3.9L4.2 20.6a.9.9 0 1 0 1.3 1.3l2.3-2.3c1.1.5 2.4.7 3.9.6 6.8-.6 11-6.4 11.2-14.8a2.1 2.1 0 0 0-2.3-2.2z"
    />
    <path d="M7.8 19.6c2.8-4.4 6.2-8 10.6-11" fill="none" stroke="#fff" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

export const LeafLine = (p: IconProps) =>
  line(
    <>
      <path d="M19.8 4.2C11.8 4.4 6.6 8.4 6 14.6c-.3 3 .9 4.9 3.4 5.2 6.4.6 10.2-5 10.4-15.6z" />
      <path d="M4.6 20.4c3-5.6 6.8-9.4 11.4-12" />
    </>,
    p,
  );

export const Syringe = (p: IconProps) =>
  line(
    <>
      <path d="m17.6 2.8 3.6 3.6M19.4 4.6l-2.2 2.2" />
      <path d="M17.6 6.4 8.5 15.5l-2.2.3.3-2.2 9.1-9.1z" />
      <path d="m15.6 4.4 4 4M11.2 8.8l1.4 1.4M9.4 10.6l1.4 1.4" />
      <path d="M6.4 17.6 3 21M2.8 17.8 6.2 21.2" />
    </>,
    p,
  );

export const Molecule = (p: IconProps) =>
  line(
    <>
      <circle cx="12" cy="12" r="1.1" fill="currentColor" stroke="none" />
      <circle cx="6.2" cy="5.8" r="1.5" />
      <circle cx="17.6" cy="6.4" r="2.6" />
      <circle cx="5.8" cy="17.8" r="2.6" />
      <circle cx="18" cy="18" r="2.6" />
      <path d="M7.3 6.9 11 11M15.8 8.2 12.8 11.2M8 15.8l3.2-3.2M15.9 15.9 12.8 12.8" />
    </>,
    p,
  );

export const Dumbbell = (p: IconProps) =>
  line(
    <>
      <path d="M8.6 12h6.8" />
      <rect x="4.8" y="7.2" width="3.4" height="9.6" rx="1" />
      <rect x="15.8" y="7.2" width="3.4" height="9.6" rx="1" />
      <path d="M2.6 9.8v4.4M21.4 9.8v4.4" />
    </>,
    p,
  );

export const Capsule = (p: IconProps) =>
  line(<rect x="3.2" y="8.6" width="17.6" height="6.8" rx="3.4" transform="rotate(-45 12 12)" />, p);

export const Clipboard = (p: IconProps) =>
  line(
    <>
      <rect x="4.8" y="4.6" width="14.4" height="16.6" rx="1.8" />
      <path d="M9 4.6V3.2h6v1.4" />
      <path d="M8.6 10.2h.01M11.4 10.2h4M8.6 14h.01M11.4 14h4M8.6 17.6h.01M11.4 17.6h2.6" />
    </>,
    p,
  );

export const Trend = (p: IconProps) =>
  line(
    <>
      <path d="M2.8 17.4 9 11.2l3.8 3.8 7.8-7.8" />
      <path d="M15.4 7h5.4v5.4" />
    </>,
    p,
  );

export const Trophy = (p: IconProps) =>
  line(
    <>
      <path d="M7.6 4h8.8v5.2a4.4 4.4 0 0 1-8.8 0z" />
      <path d="M7.6 5.8H4.8a3 3 0 0 0 3.2 3.6M16.4 5.8h2.8a3 3 0 0 1-3.2 3.6" />
      <path d="M12 13.6V17M8.4 20.4h7.2M9.6 20.4l.4-3.4h4l.4 3.4" />
    </>,
    p,
  );

export const Bulb = (p: IconProps) =>
  line(
    <>
      <path d="M9.2 17.8h5.6M10 20.8h4" />
      <path d="M8.4 14.7A6 6 0 1 1 15.6 14.7c-.7.6-.9 1.3-.9 2.1v1H9.3v-1c0-.8-.2-1.5-.9-2.1z" />
    </>,
    p,
  );

/** A square bathroom scale with its dial, the mockup's weight-loss glyph. */
export const Scale = (p: IconProps) =>
  line(
    <>
      <rect x="3.6" y="3.6" width="16.8" height="16.8" rx="3" />
      <path d="M7.6 9.2a6 6 0 0 1 8.8 0L13.6 12h-3.2z" />
    </>,
    p,
  );
